import PptxGenJS from 'pptxgenjs';

/* ── Palette (mirrors TOKENS) ──────────────────────────── */
const C = {
  bg:        '080D1A',
  card:      '0F1629',
  elevated:  '152040',
  border:    '1E3A5F',
  gold:      'C9A227',
  text:      'FAF8F5',
  textSub:   'C4CBE0',
  textMuted: '7A95BB',
  green:     '10B981',
  red:       'EF4444',
  amber:     'F59E0B',
  blue:      '3B82F6',
  slateBlue: '4A6FA5',
};

const W = 13.33; // slide width  (LAYOUT_WIDE, inches)
const H = 7.5;   // slide height

function addBg(slide) {
  slide.background = { color: C.bg };
}

function addHeader(pptx, slide, title, subtitle) {
  /* gold top bar */
  slide.addShape(pptx.ShapeType.rect, { x: 0, y: 0, w: W, h: 0.07, fill: { color: C.gold }, line: { type: 'none' } });
  slide.addText(title, {
    x: 0.55, y: 0.18, w: W - 1.1, h: 0.52,
    fontSize: 22, bold: true, color: C.text, fontFace: 'Georgia',
  });
  if (subtitle) {
    slide.addText(subtitle, {
      x: 0.55, y: 0.68, w: W - 1.1, h: 0.28,
      fontSize: 11, italic: true, color: C.textMuted, fontFace: 'Calibri',
    });
  }
  /* thin separator line */
  slide.addShape(pptx.ShapeType.line, {
    x: 0.55, y: 1.05, w: W - 1.1, h: 0,
    line: { color: C.border, pt: 0.75 },
  });
}

function addFooter(slide, text) {
  slide.addShape('rect', { x: 0, y: H - 0.32, w: W, h: 0.32, fill: { color: C.elevated }, line: { type: 'none' } });
  slide.addText(text, {
    x: 0, y: H - 0.28, w: W - 0.4, h: 0.24,
    fontSize: 8, color: C.textMuted, fontFace: 'Calibri', align: 'right',
  });
}

function kpiCard(pptx, slide, x, y, w, h, label, value, accentHex) {
  slide.addShape(pptx.ShapeType.rect, { x, y, w, h, fill: { color: C.card }, line: { color: C.border, pt: 0.75 }, rectRadius: 0.06 });
  slide.addShape(pptx.ShapeType.rect, { x, y, w: 0.045, h, fill: { color: accentHex }, line: { type: 'none' }, rectRadius: 0.02 });
  slide.addText(label.toUpperCase(), {
    x: x + 0.15, y: y + 0.16, w: w - 0.22, h: 0.24,
    fontSize: 8, bold: true, color: C.textMuted, fontFace: 'Calibri', charSpacing: 1.2,
  });
  slide.addText(value, {
    x: x + 0.15, y: y + 0.4, w: w - 0.22, h: 0.75,
    fontSize: 30, color: C.text, fontFace: 'Georgia', valign: 'middle',
  });
  slide.addShape(pptx.ShapeType.ellipse, {
    x: x + 0.15, y: y + h - 0.25, w: 0.09, h: 0.09,
    fill: { color: accentHex }, line: { type: 'none' },
  });
}

/* ─────────────────────────────────────────────────────────
   Main export function
   kpis:          [{ label, formatted, accentColor }]  (4 items)
   chartData:     [{ year, value }]
   pieDataGrants: [{ name, value, color }]
   pieDataApps:   [{ name, value, color }]
   orgName:       string
───────────────────────────────────────────────────────── */
export async function exportDashboardPPT({ kpis, chartData, pieDataGrants, pieDataApps, orgName }) {
  const pptx     = new PptxGenJS();
  pptx.layout    = 'LAYOUT_WIDE';
  pptx.author    = 'NofoDesk';
  pptx.company   = orgName || 'NofoDesk';
  pptx.subject   = 'Portfolio Dashboard';
  pptx.title     = 'Portfolio Dashboard';

  const dateStr  = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  const fileDate = new Date().toISOString().slice(0, 10);

  /* ────────────────────────────────────────────────────────
     SLIDE 1 — Title
  ──────────────────────────────────────────────────────── */
  const s1 = pptx.addSlide();
  addBg(s1);

  s1.addShape(pptx.ShapeType.rect, { x: 0, y: 0,      w: W,    h: 0.09,   fill: { color: C.gold },     line: { type: 'none' } });
  s1.addShape(pptx.ShapeType.rect, { x: 0, y: H - 0.09, w: W, h: 0.09,   fill: { color: C.elevated }, line: { type: 'none' } });
  /* vertical gold accent */
  s1.addShape(pptx.ShapeType.rect, { x: 1.2, y: 1.7,  w: 0.05, h: 4.1,   fill: { color: C.gold },     line: { type: 'none' } });

  s1.addText('PORTFOLIO',  { x: 1.55, y: 1.85, w: 10, h: 1.05, fontSize: 56, bold: true, color: C.text, fontFace: 'Georgia' });
  s1.addText('DASHBOARD',  { x: 1.55, y: 2.88, w: 10, h: 1.05, fontSize: 56, bold: true, color: C.gold, fontFace: 'Georgia' });
  s1.addText(orgName || 'Grant Portfolio Report', {
    x: 1.55, y: 4.1, w: 9, h: 0.5, fontSize: 16, italic: true, color: C.textMuted, fontFace: 'Calibri',
  });
  s1.addText(dateStr, { x: 1.55, y: 4.65, w: 9, h: 0.4, fontSize: 13, color: C.textMuted, fontFace: 'Calibri' });
  s1.addText('Generated by NofoDesk', {
    x: 0, y: H - 0.38, w: W - 0.4, h: 0.28,
    fontSize: 8, color: C.textMuted, fontFace: 'Calibri', align: 'right',
  });

  /* ────────────────────────────────────────────────────────
     SLIDE 2 — KPI Overview
  ──────────────────────────────────────────────────────── */
  const s2 = pptx.addSlide();
  addBg(s2);
  addHeader(pptx, s2, 'Portfolio Overview', 'Key performance indicators');
  addFooter(s2, `NofoDesk  ·  ${dateStr}`);

  const kpiAccents = [C.green, kpis[1]?.accentColor || C.amber, C.blue, C.green];
  const kpiXs      = [0.3, 3.55, 6.8, 10.05];
  kpis.slice(0, 4).forEach((k, i) => {
    kpiCard(pptx, s2, kpiXs[i], 1.25, 2.9, 1.6, k.label, k.formatted, kpiAccents[i]);
  });

  /* summary table */
  const tRows = [
    [
      { text: 'Metric',  options: { bold: true, color: C.textMuted, fill: C.elevated, align: 'left'  } },
      { text: 'Value',   options: { bold: true, color: C.textMuted, fill: C.elevated, align: 'right' } },
    ],
    ...kpis.slice(0, 4).map((k, i) => [
      { text: k.label,     options: { color: C.textSub, fill: i % 2 === 0 ? C.card : C.bg } },
      { text: k.formatted, options: { color: C.gold, bold: true, fill: i % 2 === 0 ? C.card : C.bg, align: 'right' } },
    ]),
  ];
  s2.addTable(tRows, {
    x: 1.6, y: 3.1, w: 10.1, colW: [7.0, 3.1], rowH: 0.55,
    fontSize: 13, fontFace: 'Calibri',
    border: { type: 'solid', color: C.border, pt: 0.5 },
    margin: [0.08, 0.2, 0.08, 0.2],
  });

  /* ────────────────────────────────────────────────────────
     SLIDE 3 — Bar Chart: Portfolio Activity
  ──────────────────────────────────────────────────────── */
  const s3 = pptx.addSlide();
  addBg(s3);
  addHeader(pptx, s3, 'Portfolio Activity', 'Annual active funding by fiscal year');
  addFooter(s3, `NofoDesk  ·  ${dateStr}`);

  if (chartData.length > 0) {
    s3.addChart(pptx.ChartType.bar, [{
      name:   'Annual Funding',
      labels: chartData.map(d => `FY ${d.year}`),
      values: chartData.map(d => Math.round(d.value)),
    }], {
      x: 0.55, y: 1.15, w: 12.2, h: 5.55,
      barDir:         'col',
      barGrouping:    'clustered',
      chartColors:    [C.gold],
      catAxisLabelColor:    C.textMuted,
      catAxisLabelFontFace: 'Calibri',
      catAxisLabelFontSize: 11,
      valAxisLabelColor:    C.textMuted,
      valAxisLabelFontFace: 'Calibri',
      valAxisLabelFontSize: 10,
      valAxisLabelFormatCode: '$#,##0,,M',
      showLegend:     false,
      chartAreaBkgndColor: C.bg,
      plotAreaBkgndColor:  C.card,
      catGridLine:    { style: 'none' },
      valGridLine:    { style: 'dash', color: C.border, pt: 0.5 },
      catAxisLineShow: false,
      valAxisLineShow: false,
      showValue:      true,
      dataLabelColor: C.text,
      dataLabelFontFace: 'Calibri',
      dataLabelFontSize: 9,
      valAxisDisplayUnit: 'millions',
    });
  }

  /* ────────────────────────────────────────────────────────
     SLIDE 4 — Donut Charts (Grants + Applications)
  ──────────────────────────────────────────────────────── */
  const s4 = pptx.addSlide();
  addBg(s4);
  addHeader(pptx, s4, 'Portfolio & Applications Breakdown', 'Distribution by lifecycle stage and award status');
  addFooter(s4, `NofoDesk  ·  ${dateStr}`);

  /* divider */
  s4.addShape(pptx.ShapeType.line, {
    x: 6.66, y: 1.15, w: 0, h: 5.8,
    line: { color: C.border, pt: 0.75 },
  });

  if (pieDataGrants.length > 0) {
    s4.addText('GRANTS BY LIFECYCLE STAGE', {
      x: 0.4, y: 1.18, w: 5.9, h: 0.3,
      fontSize: 8.5, bold: true, color: C.textMuted, fontFace: 'Calibri', charSpacing: 1.5,
    });
    s4.addChart(pptx.ChartType.doughnut, [{
      name:   'Grants',
      labels: pieDataGrants.map(d => d.name),
      values: pieDataGrants.map(d => d.value),
    }], {
      x: 0.4, y: 1.48, w: 5.9, h: 5.2,
      holeSize:        55,
      chartColors:     pieDataGrants.map(d => d.color.replace('#', '')),
      showLegend:      true,
      legendPos:       'b',
      legendColor:     C.textSub,
      legendFontFace:  'Calibri',
      legendFontSize:  11,
      showPercent:     true,
      showLabel:       false,
      showValue:       false,
      dataLabelColor:  C.text,
      dataLabelFontFace: 'Calibri',
      dataLabelFontSize: 10,
      chartAreaBkgndColor: C.bg,
      plotAreaBkgndColor:  C.bg,
    });
  }

  if (pieDataApps.length > 0) {
    s4.addText('APPLICATIONS BY AWARD STATUS', {
      x: 7.0, y: 1.18, w: 5.9, h: 0.3,
      fontSize: 8.5, bold: true, color: C.textMuted, fontFace: 'Calibri', charSpacing: 1.5,
    });
    s4.addChart(pptx.ChartType.doughnut, [{
      name:   'Applications',
      labels: pieDataApps.map(d => d.name),
      values: pieDataApps.map(d => d.value),
    }], {
      x: 7.0, y: 1.48, w: 5.9, h: 5.2,
      holeSize:        55,
      chartColors:     pieDataApps.map(d => d.color.replace('#', '')),
      showLegend:      true,
      legendPos:       'b',
      legendColor:     C.textSub,
      legendFontFace:  'Calibri',
      legendFontSize:  11,
      showPercent:     true,
      showLabel:       false,
      showValue:       false,
      dataLabelColor:  C.text,
      dataLabelFontFace: 'Calibri',
      dataLabelFontSize: 10,
      chartAreaBkgndColor: C.bg,
      plotAreaBkgndColor:  C.bg,
    });
  }

  /* ────────────────────────────────────────────────────────
     SLIDE 5 — Annual Funding Data Table
  ──────────────────────────────────────────────────────── */
  if (chartData.length > 0) {
    const s5 = pptx.addSlide();
    addBg(s5);
    addHeader(pptx, s5, 'Annual Funding Detail', 'Source data for the Portfolio Activity chart');
    addFooter(s5, `NofoDesk  ·  ${dateStr}`);

    const dataRows = [
      [
        { text: 'Fiscal Year',    options: { bold: true, color: C.textMuted, fill: C.elevated, align: 'center' } },
        { text: 'Annual Funding', options: { bold: true, color: C.textMuted, fill: C.elevated, align: 'right'  } },
      ],
      ...chartData.map((d, i) => [
        { text: `FY ${d.year}`,                               options: { color: C.textSub, fill: i % 2 === 0 ? C.card : C.bg, align: 'center' } },
        { text: `$${Math.round(d.value).toLocaleString()}`,   options: { color: C.gold, bold: true, fill: i % 2 === 0 ? C.card : C.bg, align: 'right'  } },
      ]),
    ];

    s5.addTable(dataRows, {
      x: 2.2, y: 1.15, w: 8.9,
      colW: [4.45, 4.45],
      rowH: 0.52,
      fontSize: 13, fontFace: 'Calibri',
      border: { type: 'solid', color: C.border, pt: 0.5 },
      margin: [0.08, 0.2, 0.08, 0.2],
    });
  }

  await pptx.writeFile({ fileName: `Portfolio_Dashboard_${fileDate}.pptx` });
}
